"use client";
import { TacticLoader } from "@cmp-src/loaders/TacticLoader/TacticLoader";
import {
  AiChatToolShowWorkplaceRequestsOutput,
  AiChatToolShowWorkplaceRequestsPart,
  TessaWorkplaceRequest,
  WorkplaceRequestStatus
} from "@gettactic/api";
import { MARIN_ROUTES } from "@gettactic/defines/src/web-routes";
import { format, formatDistanceToNow } from "date-fns";
import Link from "next/link";
import React from "react";
import { ChatBotToolCard } from "@/features/tessa/components/ChatBotToolCard";
import { TessaMessageRendererProps } from "@/features/tessa/types";
import {
  getRequestStatusColor,
  RequestStatusPill
} from "@/features/workplace-requests/components/RequestStatusPill";

const STATUS_LABELS: Record<WorkplaceRequestStatus, string> = {
  open: "Open",
  pending: "Pending",
  closed: "Closed",
  archived: "Archived"
};

const requestPlace = (request: TessaWorkplaceRequest) =>
  [request.location, request.floor, request.office].filter(Boolean).join(" · ");

const RequestActivity: React.FC<{
  activity: NonNullable<TessaWorkplaceRequest["activity"]>;
}> = ({ activity }) => (
  <ol className="flex flex-col gap-3 border-neutral-200 border-l pl-4">
    {activity.map((entry) => (
      <li
        key={`${entry.at}-${entry.change ?? entry.comment}`}
        className="relative text-sm"
      >
        <span className="absolute top-1.5 -left-[21px] size-2 rounded-full bg-neutral-400" />
        <p className="text-neutral-500 text-xs">
          {format(new Date(entry.at), "MMM d, HH:mm")}
          {entry.by ? ` · ${entry.by}` : ""}
        </p>
        {entry.comment ? (
          <p className="mt-1 whitespace-pre-line rounded-md bg-neutral-50 px-3 py-2 text-neutral-800">
            {entry.comment}
          </p>
        ) : (
          <p className="text-neutral-800">{entry.change}</p>
        )}
      </li>
    ))}
  </ol>
);

const RequestRow: React.FC<{ request: TessaWorkplaceRequest }> = ({
  request
}) => {
  const place = requestPlace(request);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <Link
          href={`${MARIN_ROUTES.workplaceRequests}/${request.number}`}
          className="font-semibold text-neutral-900 hover:underline"
        >
          #{request.number} {request.title}
        </Link>
        <RequestStatusPill
          color={getRequestStatusColor(request.status)}
          label={
            request.sub_status ||
            STATUS_LABELS[request.status] ||
            request.status
          }
        />
        {request.priority === "high" && (
          <span className="rounded-full bg-red-50 px-2 py-0.5 font-medium text-red-700 text-xs">
            High priority
          </span>
        )}
      </div>
      <p className="text-neutral-600 text-sm">
        {[request.type, place].filter(Boolean).join(" · ")}
      </p>
      <p className="text-neutral-500 text-xs">
        {request.assigned_to
          ? `Assigned to ${request.assigned_to}`
          : "Not assigned yet"}
        {" · "}
        Updated{" "}
        {formatDistanceToNow(new Date(request.updated), { addSuffix: true })}
      </p>
    </div>
  );
};

export const ChatBotToolShowWorkplaceRequests: React.FC<
  TessaMessageRendererProps<AiChatToolShowWorkplaceRequestsPart>
> = ({ part }) => {
  const output = JSON.parse(part?.output || "null") as
    | AiChatToolShowWorkplaceRequestsOutput
    | undefined;

  if (!output) return <TacticLoader className="opacity-60" />;

  const byId = output._metadata?.workplace_requests_by_id ?? {};
  const requests = (output.request_ids ?? [])
    .map((id) => byId[id])
    .filter((request): request is TessaWorkplaceRequest => !!request);

  if (requests.length === 0) return null;

  const single = requests.length === 1 ? requests[0] : undefined;

  return (
    <ChatBotToolCard toolId={part.toolCallId}>
      <div className="flex flex-col gap-4">
        <ul className="flex flex-col divide-y divide-neutral-100">
          {requests.map((request) => (
            <li key={request.id} className="py-3 first:pt-0 last:pb-0">
              <RequestRow request={request} />
            </li>
          ))}
        </ul>
        {single?.activity && single.activity.length > 0 && (
          <RequestActivity activity={single.activity} />
        )}
      </div>
    </ChatBotToolCard>
  );
};
